import { useCallback } from 'react';
import { Metrics } from '../utils/metrics';
import { captureException } from '../utils/sentryUtils';
import { chirpApprovalToApprovalData, userIdsToChirpApprovalSteps } from '../utils/chirpApprovalAdapters';
import { useCreateApprovalMutation, useCreateBulkApprovalMutation } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsRpc.hooks';
import { GET_LATEST_APPROVAL_QUERY } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsRpc.dfc.new';
export const useChirpCreateApproval = ({
  approvalType,
  approvalAction,
  approvableObjectId,
  assetName,
  approvalObjectIds,
  usersRecord
}) => {
  const [chirpCreateApproval] = useCreateApprovalMutation();
  const [chirpCreateBulkApproval] = useCreateBulkApprovalMutation();
  const create = useCallback((note, approverOwnerIds, approvalRules, dueDate, title) => {
    /*
     * Currently the Bulk approval creation flow that used when approvalObjectIds are
     * passed to the ApprovalsProvider is only used in Social Posts/Broadcasts
     */
    if (approvalObjectIds) {
      const approvalRequests = approvalObjectIds.map(objectId => Object.assign({
        approvalType,
        approvalAction,
        approvableObjectId: objectId,
        approvalSteps: userIdsToChirpApprovalSteps(approverOwnerIds),
        approvalRules,
        title: title !== null && title !== void 0 ? title : assetName,
        requestNote: note
      }, dueDate && {
        dueDate
      }));
      /*
       * No cache write or refetch call — bulk approval creates span multiple approvableObjectIds
       * and caller handles the response directly
       */
      return chirpCreateBulkApproval({
        variables: {
          approvalRequests
        }
      }).then(result => {
        var _result$data, _response$createdAppr, _response$failedReque;
        Metrics.counter('request-created').increment();
        const response = result === null || result === void 0 || (_result$data = result.data) === null || _result$data === void 0 ? void 0 : _result$data.result;
        return {
          createdApprovals: ((_response$createdAppr = response === null || response === void 0 ? void 0 : response.createdApprovals) !== null && _response$createdAppr !== void 0 ? _response$createdAppr : []).map(chirpApproval => chirpApprovalToApprovalData(chirpApproval, usersRecord, approvalType, approvalAction)),
          failedApprovals: ((_response$failedReque = response === null || response === void 0 ? void 0 : response.failedRequests) !== null && _response$failedReque !== void 0 ? _response$failedReque : []).map(req => {
            var _req$approvalSteps, _req$requestNote, _req$approvalRules;
            return {
              approvalType: req.approvalType,
              approvalAction: req.approvalAction,
              approvableObjectId: req.approvableObjectId,
              title: req.title,
              approverOwnerIds: ((_req$approvalSteps = req.approvalSteps) !== null && _req$approvalSteps !== void 0 ? _req$approvalSteps : []).map(step => Number(step.actor.id)),
              note: (_req$requestNote = req.requestNote) !== null && _req$requestNote !== void 0 ? _req$requestNote : '',
              approvalRules: String((_req$approvalRules = req.approvalRules) !== null && _req$approvalRules !== void 0 ? _req$approvalRules : '')
            };
          })
        };
      }).catch(error => {
        captureException(error, {
          extra: {
            fingerprint: 'useApprovalData-createBulk-chirp',
            approvalType,
            approvalAction
          }
        });
        throw error;
      });
    }
    return chirpCreateApproval({
      variables: Object.assign({
        approvalType,
        approvalAction,
        approvableObjectId: approvableObjectId,
        approvalSteps: userIdsToChirpApprovalSteps(approverOwnerIds),
        approvalRules,
        title: assetName,
        requestNote: note
      }, dueDate && {
        dueDate
      }),
      update: (cache, {
        data: mutationData
      }) => {
        var _mutationData$result;
        const approvalData = mutationData === null || mutationData === void 0 || (_mutationData$result = mutationData.result) === null || _mutationData$result === void 0 ? void 0 : _mutationData$result.approval;
        if (approvalData) {
          cache.writeQuery({
            query: GET_LATEST_APPROVAL_QUERY,
            variables: {
              approvalType,
              approvalAction,
              approvableObjectId: approvableObjectId
            },
            data: {
              result: {
                approval: approvalData
              }
            }
          });
        }
      }
    }).then(result => {
      var _result$data2;
      Metrics.counter('request-created').increment();
      const approval = result === null || result === void 0 || (_result$data2 = result.data) === null || _result$data2 === void 0 || (_result$data2 = _result$data2.result) === null || _result$data2 === void 0 ? void 0 : _result$data2.approval;
      if (approval) {
        return chirpApprovalToApprovalData(approval, usersRecord, approvalType, approvalAction);
      } else {
        throw new Error('CHIRP createApproval succeeded but returned no approval data');
      }
    }).catch(error => {
      captureException(error, {
        extra: {
          fingerprint: 'useApprovalData-create-chirp',
          approvalType,
          approvalAction,
          approvableObjectId
        }
      });
      throw error;
    });
  }, [approvalType, approvalAction, approvableObjectId, assetName, approvalObjectIds, chirpCreateApproval, chirpCreateBulkApproval, usersRecord]);
  return create;
};